import React from 'react';
import PageSEO from '@/components/PageSEO';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, FileText, Mail, Phone, CheckCircle } from 'lucide-react';
import Navbar from '@/components/trinity/Navbar';
import Footer from '@/components/trinity/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import StructuredData from '@/components/StructuredData';

const privacySchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Privacy Policy — Trinity Dental Arts",
  "url": "https://trinitydentalarts.com/PrivacyPolicy",
  "description": "Trinity Dental Arts Privacy Policy and HIPAA Notice of Privacy Practices. Learn how we collect, use, and protect your personal and health information.",
  "datePublished": "2026-02-14",
  "dateModified": "2026-04-14",
  "publisher": {
    "@type": "Dentist",
    "name": "Trinity Dental Arts",
    "url": "https://trinitydentalarts.com",
    "telephone": "+1-727-228-6846",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "11105 Trinity Blvd",
      "addressLocality": "Trinity",
      "addressRegion": "FL",
      "postalCode": "34655",
      "addressCountry": "US"
    }
  },
  "about": {
    "@type": "Thing",
    "name": "Privacy Policy",
    "description": "Dental practice privacy policy and patient rights"
  }
};

export default function PrivacyPolicy() {

  const sections = [
    {
      icon: Shield,
      title: "HIPAA Compliance",
      content: "Trinity Dental Arts is committed to maintaining the privacy and security of your protected health information (PHI) in compliance with the Health Insurance Portability and Accountability Act (HIPAA). We implement appropriate administrative, physical, and technical safeguards to protect your information."
    },
    {
      icon: Eye,
      title: "Why and How We Collect Information",
      content: "We collect personal and health information to provide you with quality dental care, process payments, communicate with you about appointments, and comply with legal requirements. Information is collected directly from you through forms, conversations, examinations, and diagnostic procedures. We also collect technical information automatically when you visit our website to improve user experience and site functionality. Specifically, we collect:",
      list: [
        "Personal Information: Name, date of birth, contact information, insurance details",
        "Health Information: Medical and dental history, treatment records, diagnostic information, prescriptions",
        "Financial Information: Payment card details, billing information, insurance claims",
        "Technical Information: IP address, browser type, device information, cookies for website functionality"
      ]
    },
    {
      icon: Lock,
      title: "How We Use Your Information",
      content: [
        "Treatment: To provide, coordinate, and manage your dental care",
        "Payment: To process billing, insurance claims, and payment transactions",
        "Healthcare Operations: To improve quality of care, train staff, and conduct business operations",
        "Appointment Reminders: To send you appointment confirmations and reminders",
        "Legal Requirements: To comply with applicable laws and regulations"
      ]
    },
    {
      icon: FileText,
      title: "Information Sharing",
      content: "We do not sell your personal or health information. Consumer information is not shared with third-parties for marketing purposes. We may share your information only in the following circumstances:",
      list: [
        "Healthcare Providers: With other healthcare professionals involved in your care",
        "Insurance Companies: For billing and claims processing",
        "Business Associates: With HIPAA-compliant service providers who assist in our operations",
        "Legal Obligations: When required by law, court order, or government authority",
        "Emergency Situations: To protect your vital interests in medical emergencies"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Title, description, canonical, Open Graph and Twitter tags (PageSEO also applies pageContent overrides). */}
      <PageSEO
        path="/privacy-policy"
        title="Privacy Policy & HIPAA Notice | Trinity Dental Arts"
        description="Read the privacy policy for Trinity Dental Arts in Trinity, FL. This page explains how we collect, use and protect your personal information."
      />
      <StructuredData data={privacySchema} />
      <Navbar />
      
      <div className="container mx-auto px-6 py-16 max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Trust Signals Banner */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {[
              { icon: Shield, label: 'Patient Privacy', color: 'bg-blue-50 border-blue-200 text-blue-800' },
              { icon: Lock, label: 'HTTPS Encrypted', color: 'bg-green-50 border-green-200 text-green-800' },
              { icon: CheckCircle, label: 'No Data Selling', color: 'bg-purple-50 border-purple-200 text-purple-800' },
              { icon: FileText, label: 'Transparent Disclosure', color: 'bg-amber-50 border-amber-200 text-amber-800' },
            ].map(({ icon: Icon, label, color }) => (
              <div key={label} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border font-medium text-sm ${color}`}>
                <Icon className="w-4 h-4" />
                {label}
              </div>
            ))}
          </div>

          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 bg-blue-100 rounded-full px-6 py-3 mb-6">
              <Shield className="w-6 h-6 text-blue-600" />
              <span className="text-blue-900 font-semibold">Patient Privacy</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-slate-600">
              Effective Date: February 14, 2026 | Last Updated: September 8, 2026
            </p>
          </div>

          {/* Introduction */}
          <Card className="mb-8">
            <CardContent className="p-8">
              <p className="text-slate-700 leading-relaxed mb-4">
                At Trinity Dental Arts, we take your privacy seriously. This Privacy Policy explains how we collect, 
                use, disclose, and safeguard your information when you visit our website or receive our dental services. 
                We are committed to protecting your protected health information (PHI) in accordance with the Health 
                Insurance Portability and Accountability Act (HIPAA) and other applicable privacy laws.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Using this website does not authorize disclosure of your health information for advertising. You can decline optional analytics and still contact us or request a consultation online.
              </p>
            </CardContent>
          </Card>

          {/* Main Sections */}
          {sections.map((section, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1, duration: 0.5 }}
            >
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <section.icon className="w-5 h-5 text-blue-600" />
                    </div>
                    {section.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {typeof section.content === 'string' ? (
                    <p className="text-slate-700 leading-relaxed">{section.content}</p>
                  ) : (
                    <ul className="space-y-3">
                      {section.content.map((item, i) => (
                        <li key={i} className="flex gap-3">
                          <span className="text-blue-600 mt-1">•</span>
                          <span className="text-slate-700 leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.list && (
                    <ul className="space-y-3 mt-4">
                      {section.list.map((item, i) => (
                        <li key={i} className="flex gap-3">
                          <span className="text-blue-600 mt-1">•</span>
                          <span className="text-slate-700 leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}

          {/* Additional Sections */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Your Privacy Rights</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 mb-4">Under HIPAA, you have the right to:</p>
              <ul className="space-y-3">
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Access and obtain a copy of your health records</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Request corrections to your health information</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Receive an accounting of disclosures of your health information</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Request restrictions on uses and disclosures of your health information</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Request confidential communications of health information</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">File a complaint if you believe your privacy rights have been violated</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Security Measures</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-4">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="space-y-3">
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">HTTPS encryption for data transmission</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Secure, encrypted storage of electronic health records</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Regular security audits and risk assessments</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Employee training on HIPAA compliance and privacy practices</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Physical safeguards to protect paper records and computer systems</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Business associate agreements with all third-party service providers</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Website Cookies and Tracking</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-4">
                Our website uses cookies and similar tracking technologies to enhance your browsing experience. 
                These technologies help us understand how visitors use our website and improve our services. 
                We use:
              </p>
              <ul className="space-y-3">
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Essential cookies for website functionality</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Google Analytics and Google Ads measurement use cookies and advertising click identifiers to measure visits and advertising results. This measurement is enabled by default on eligible public pages based on consent obtained directly by our practice. A saved Decline choice, Global Privacy Control or Do Not Track signal disables it. Google ad personalization is disabled, and form answers and contact details are not included in our Google events.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">With your optional cookie consent: OpenAI, Vibe, RetargetIQ and GrowDental advertising tools on eligible public pages. These providers may use cookies, device or network information, advertising identifiers, and browsing activity. RetargetIQ may recognize visitors using matched identifiers. These tools are excluded from patient intake pages; Google events do not include form answers or contact details.</span>
                </li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">
                Use Cookie Preferences in the footer to accept or decline optional tracking. Declining leaves essential website features available. You can also manage stored cookies through your browser settings. We respect Global Privacy Control and Do Not Track signals for optional measurement.
              </p>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Phone className="w-5 h-5 text-blue-600" />
                </div>
                SMS Text Messaging Consent
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-4">
                By providing your phone number and opting in to receive SMS text messages from Trinity Dental Arts, you consent to the following:
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-4">
                <p className="text-slate-800 font-medium mb-3">
                  "I consent to receive SMS text messages from Trinity Dental Arts for appointment reminders, marketing messages, and general two-way communication."
                </p>
              </div>
              <ul className="space-y-3 mb-4">
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700"><strong>Message Frequency:</strong> Msg frequency varies. Msg&data rates may apply.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700"><strong>Opt-Out Instructions:</strong> Reply HELP for support. Reply STOP to opt out.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700"><strong>Privacy:</strong> See our privacy policy for more information on how we handle your data.</span>
                </li>
              </ul>
              <p className="text-slate-700 leading-relaxed text-sm">
                You can opt out of receiving SMS messages at any time by replying STOP to any message. Standard message and data rates may apply depending on your carrier. Trinity Dental Arts will not share your phone number with third parties for marketing purposes without your explicit consent.
              </p>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Children's Privacy</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed">
                Our services are not directed to children under 13 years of age without parental consent. 
                We do not knowingly collect personal information from children under 13 without verifiable 
                parental consent. If you are a parent or guardian and believe we have collected information 
                from your child without consent, please contact us immediately.
              </p>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Data Retention</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-4">
                We retain your personal and health information only as long as necessary to fulfill the purposes described in this policy and to comply with applicable legal obligations:
              </p>
              <ul className="space-y-3">
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700"><strong>Patient Health Records (PHI):</strong> Retention depends on applicable law, the type of record, and our approved clinical records schedule. Contact our Privacy Officer for the schedule that applies to your records.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700"><strong>Website Inquiry & Lead Data:</strong> Used for follow-up and care coordination; contact our Privacy Officer to request access or deletion where permitted.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700"><strong>Website Analytics Data:</strong> Optional first-party measurements exclude patient form answers. Session identifiers are pseudonymous and are not a guarantee of anonymity.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700"><strong>Financial Records:</strong> Billing records follow applicable financial and clinical retention requirements. Enter payment details only in the designated payment service.</span>
                </li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">
                You may request deletion of non-clinical data by contacting our Privacy Officer. Some records must be retained for care, legal obligations, backups, or an active legal hold; deletion is subject to those requirements.
              </p>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Data Breach Notification</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-4">
                In the event of a breach of unsecured protected health information, Trinity Dental Arts will comply fully with the HIPAA Breach Notification Rule (45 CFR §§ 164.400–414):
              </p>
              <ul className="space-y-3">
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700"><strong>Individual Notification:</strong> Affected individuals will be notified in writing (by first-class mail or email if previously authorized) without unreasonable delay and no later than <strong>60 calendar days</strong> after discovery of the breach.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700"><strong>HHS Notification:</strong> We will notify the U.S. Department of Health and Human Services Office for Civil Rights (OCR) of all breaches. Breaches affecting 500 or more individuals will be reported immediately; smaller breaches will be logged and reported annually.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700"><strong>Media Notification:</strong> If a breach affects 500 or more residents of Florida, we will provide notice to prominent media outlets in the state within 60 days.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700"><strong>Breach Notice Content:</strong> Notices will include a description of what happened, the types of information involved, steps individuals should take to protect themselves, what we are doing to investigate and mitigate harm, and contact information for questions.</span>
                </li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">
                To report a suspected breach or privacy concern, contact our Privacy Officer immediately at <a href="mailto:privacy@trinitydentalarts.com" className="text-blue-600 underline">privacy@trinitydentalarts.com</a> or call <a href="tel:7272286846" className="text-blue-600 underline">(727) 228-6846</a>.
              </p>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Changes to This Privacy Policy</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices or 
                legal requirements. We will notify you of any material changes by posting the updated policy 
                on our website with a new effective date. We encourage you to review this policy periodically.
              </p>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Third-Party Links</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the privacy 
                practices or content of these external sites. We encourage you to review the privacy policies 
                of any third-party sites you visit.
              </p>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="bg-gradient-to-br from-blue-50 to-slate-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <Mail className="w-6 h-6 text-blue-600" />
                Contact Us About Privacy
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-6">
                If you have questions about this Privacy Policy, wish to exercise your privacy rights, 
                or need to file a complaint, please contact us:
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">Email</div>
                    <a href="mailto:privacy@trinitydentalarts.com" className="text-blue-600 hover:text-blue-700">
                      privacy@trinitydentalarts.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">Phone</div>
                    <a href="tel:7272286846" className="text-blue-600 hover:text-blue-700">
                      (727) 228-6846
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">Mailing Address</div>
                    <div className="text-slate-700">
                      Trinity Dental Arts<br />
                      Privacy Officer<br />
                      11105 Trinity Blvd<br />
                      Trinity, FL 34655
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-slate-700">
                  <strong>Note:</strong> You may also file a complaint with the U.S. Department of Health and Human Services 
                  Office for Civil Rights if you believe your privacy rights have been violated. Filing a complaint will not 
                  affect your care at Trinity Dental Arts.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Notice of Privacy Practices */}
          <Card className="bg-slate-900 text-white">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-8 h-8 text-blue-400" />
                <h3 className="text-2xl font-bold">Notice of Privacy Practices</h3>
              </div>
              <p className="text-slate-300 leading-relaxed mb-4">
                Our complete Notice of Privacy Practices (NPP) provides detailed information about how we may use 
                and disclose your protected health information. You have the right to receive a paper copy of our 
                NPP at any time.
              </p>
              <p className="text-slate-300 leading-relaxed">
                To request a copy or for more information about our HIPAA policies, please contact our office 
                at (727) 228-6846 or visit us at 11105 Trinity Blvd, Trinity, FL 34655.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
}